// MarkdownRenderer.jsx
import React, { useEffect, useRef, useState } from "react";
import MarkdownIt from "markdown-it";
import DOMPurify from "dompurify";
import { TYPO } from "styles/Typo";
import { useRouter } from "next/router";
import {
  DISCUSS_HTML_SANITIZE,
  youtubeEmbedPlugin,
} from "./youtubeEmbed";
const md = new MarkdownIt({
  breaks: true,
  html: false,
});
md.use(youtubeEmbedPlugin);

// 하이라이트 문법 처리 추가
md.use(function (md) {
  const highlightRule = /@@(.+?)@@/g;
  const boldRule = /\*\*(.+?)\*\*/g;

  md.renderer.rules.text = function (
    tokens: any,
    idx: any,
    options: any,
    env: any,
    self: any
  ) {
    const content = tokens[idx].content;
    return content
      .replace(
        highlightRule,
        "<span style='background-color: rgba(96, 200, 0, 0.2); ' class='!text-gray90'>$1</span>"
      )
      .replace(boldRule, "<strong style='font-weight: 600;'>$1</strong>");
  };

  md.renderer.rules.heading_open = function (
    tokens: any,
    idx: any,
    options: any,
    env: any,
    self: any
  ) {
    const level = tokens[idx].tag;
    switch (level) {
      case "h2":
        return `<h2 class='text-gray90 pt-[64px]' style="font-size: 26px; font-family: Pretendard; font-weight: 600; line-height: 160%; letter-spacing: -0.78px; word-wrap: break-word; word-break: keep-all; white-space: normal; overflow-wrap: break-word;">`;

      default:
        return `<p class='${TYPO.TYPO7_MD} '>`;
    }
  };
  md.renderer.rules.paragraph_open = function (
    tokens: any,
    idx: any,
    options: any,
    env: any,
    self: any
  ) {
    return `<p class=' ' style='font-family: Pretendard; word-wrap: break-word; word-break: keep-all; white-space: normal; overflow-wrap: break-word;'>`;
  };

  md.renderer.rules.blockquote_open = function (
    tokens: any,
    idx: any,
    options: any,
    env: any,
    self: any
  ) {
    // return `<blockquote class='p-4 bg-gray2 rounded-[12px]'>`;
    return `<blockquote class=''>`;
  };
  md.renderer.rules.strong_open = function (
    tokens: any,
    idx: any,
    options: any,
    env: any,
    self: any
  ) {
    return `<strong class='font-[600] !text-gray80'>`;
  };
});

function MarkdownRenderer({
  description,
  type,
  isLoginUser,
}: {
  description: string;
  type: "summary" | "introduction" | "contents";
  isLoginUser?: boolean;
}) {
  const [clientHtml, setClientHtml] = useState("");
  const [isClient, setIsClient] = useState(false);

  // 서버사이드와 초기 클라이언트 렌더링을 위한 HTML
  const responseFromLLM = description ? description : "";
  const rawHtml = md.render(responseFromLLM);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // 클라이언트에서만 텍스트 자르기 적용 (하이드레이션 에러 방지)
  // useEffect(() => {
  //   if (isClient && type === "contents" && !isLoginUser && description) {
  //     const truncatedContent = description.slice(0, 300) + "...";
  //     setDisplayContent(truncatedContent);
  //   } else {
  //     setDisplayContent(description);
  //   }
  // }, [isClient, type, isLoginUser, description]);

  useEffect(() => {
    if (isClient && description) {
      // 클라이언트에서 추가 보안을 위해 DOMPurify 적용
      if (typeof window !== "undefined") {
        const cleanHtml = DOMPurify.sanitize(
          md.render(description),
          DISCUSS_HTML_SANITIZE
        );
        setClientHtml(cleanHtml);
      }
    }
  }, [isClient, description]);

  if (!description) return null;

  // 클라이언트에서는 sanitized HTML, 서버사이드에서는 rawHtml 사용
  const htmlToRender = isClient && clientHtml ? clientHtml : rawHtml;

  return (
    <section
      className="prose discuss-report-markdown"
      id={`discuss-report-markdown-${type}`}
      dangerouslySetInnerHTML={{ __html: htmlToRender }}
      data-testid={`discuss-report-markdown-${type}`}
      suppressHydrationWarning={type === "contents" && !isLoginUser} // contents에서만 하이드레이션 경고 억제
    />
  );
}

export default function Research({
  detailDiscussData,
  isLoginUser,
}: {
  detailDiscussData: any;
  isLoginUser: boolean;
}) {
  const ctaRef = useRef<HTMLDivElement>(null);
  const [isClient, setIsClient] = useState(false);
  const summary = detailDiscussData?.summary ? detailDiscussData?.summary : "";
  const introduction = detailDiscussData?.intro ? detailDiscussData?.intro : "";
  const contents = detailDiscussData?.contents
    ? detailDiscussData?.contents
    : "";
  const router = useRouter();
  useEffect(() => {
    setIsClient(true);
  }, []);
  if (
    !detailDiscussData ||
    !detailDiscussData?.summary ||
    !detailDiscussData?.intro ||
    !detailDiscussData?.contents
  )
    return null;

  return (
    <article className="flex flex-col ">
      <div className="px-6">
        <MarkdownRenderer
          description={introduction}
          type="introduction"
          isLoginUser={isLoginUser}
        />
      </div>

      <div
        className="relative "
        style={{
          minHeight: !isLoginUser
            ? ctaRef.current
              ? ctaRef.current?.clientHeight + 220 + "px"
              : "560px"
            : "560px",
        }}
      >
        <div className="px-6">
          <MarkdownRenderer
            description={contents}
            type="contents"
            isLoginUser={isLoginUser}
          />
        </div>

        <div id="report-last-area"></div>
      </div>
    </article>
  );
}

const AIIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="30"
      height="30"
      viewBox="0 0 30 30"
      fill="none"
    >
      <path
        d="M13.1366 7.10086C13.1366 6.07866 13.9653 5.25 14.9875 5.25C16.0097 5.25 16.8384 6.07866 16.8384 7.10086V7.71781C16.8384 8.74001 16.0097 9.56867 14.9875 9.56867C13.9653 9.56867 13.1366 8.74001 13.1366 7.71781V7.10086Z"
        fill="#FFCA45"
      />
      <path
        d="M4.5 14.8105C4.5 13.6179 5.46677 12.6512 6.65934 12.6512C7.85191 12.6512 8.81867 13.6179 8.81867 14.8105V16.6614C8.81867 17.8539 7.85191 18.8207 6.65934 18.8207C5.46677 18.8207 4.5 17.8539 4.5 16.6614V14.8105Z"
        fill="#F49943"
      />
      <path
        d="M21.1611 14.8107C21.1611 13.6181 22.1279 12.6514 23.3205 12.6514C24.513 12.6514 25.4798 13.6181 25.4798 14.8107V16.6616C25.4798 17.8541 24.513 18.8209 23.3205 18.8209C22.1279 18.8209 21.1611 17.8541 21.1611 16.6616V14.8107Z"
        fill="#F49943"
      />
      <path
        d="M13.7583 10.186C13.0769 10.186 12.5244 9.6336 12.5244 8.95213V7.71822C12.5244 7.03675 13.0769 6.48432 13.7583 6.48432H16.2261C16.9076 6.48432 17.46 7.03675 17.46 7.71822V8.95213C17.46 9.6336 16.9076 10.186 16.2261 10.186H13.7583Z"
        fill="#F49943"
      />
      <path
        d="M6.9707 10.6898C6.9707 9.05001 8.30001 7.7207 9.93979 7.7207H20.0424C21.6822 7.7207 23.0115 9.05001 23.0115 10.6898V21.4094C23.0115 23.0491 21.6822 24.3784 20.0424 24.3784H9.93979C8.30001 24.3784 6.9707 23.0491 6.9707 21.4094V10.6898Z"
        fill="#FFE062"
      />
      <path
        d="M12.2129 19.1332C12.2129 18.6221 12.6272 18.2078 13.1383 18.2078H16.84C17.3511 18.2078 17.7655 18.6221 17.7655 19.1332C17.7655 19.6443 17.3511 20.0586 16.84 20.0586H13.1383C12.6272 20.0586 12.2129 19.6443 12.2129 19.1332Z"
        fill="white"
      />
      <path
        d="M16.5312 14.5056C16.5312 13.8242 17.0837 13.2717 17.7652 13.2717C18.4466 13.2717 18.9991 13.8242 18.9991 14.5056C18.9991 15.1871 18.4466 15.7395 17.7652 15.7395C17.0837 15.7395 16.5312 15.1871 16.5312 14.5056Z"
        fill="var(--gray90)"
      />
      <path
        d="M10.9775 14.5056C10.9775 13.8242 11.53 13.2717 12.2114 13.2717C12.8929 13.2717 13.4454 13.8242 13.4454 14.5056C13.4454 15.1871 12.8929 15.7395 12.2114 15.7395C11.53 15.7395 10.9775 15.1871 10.9775 14.5056Z"
        fill="var(--gray90)"
      />
    </svg>
  );
};
